<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('users.usersLabel') }}</span>
        <h3 class="page-title">{{ $t('users.usersList') }}</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="$router.push('/uzytkownicy/nowy')">
          <i class="material-icons">add</i>
          {{ $t('users.addUser') }}
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      v-if="currentUserID > 0"
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="loaded"
      :view-name="'users'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('ui.nameAndSurename') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.emailAddress') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('users.accountType') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('users.isAccountActive') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('settings.userGroups.userGroups') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded && currentUserID">
          <tr
            v-for="(user, index) of users"
            :key="'user-' + index">
            <td class="align-middle">
              {{ user.name }}
              <template v-if="user.customer_tax_number">
                <br>
                <small>
                  {{ $t('users.customerTaxNumber') }}
                </small>:
                <small class="text-primary">
                  {{ user.customer_tax_number }}
                </small>
              </template>
            </td>
            <td class="align-middle">
              {{ user.email }}
            </td>
            <td class="align-middle">
              <strong
                v-if="user.type === 'admin'"
                class="font-weight-bold text-danger">
                {{ $t('users.admin') }}
              </strong>
              <span
                v-if="user.type === 'worker'"
                class="text-primary">
                {{ $t('users.worker') }}
              </span>
              <span
                v-if="user.type === 'warehouseman'"
                class="text-secondary">
                {{ $t('users.realWarehouseman') }}
              </span>
              <span
                v-if="user.type === 'warehouse_manager'"
                class="text-secondary">
                {{ $t('users.warehouseman') }}
              </span>
              <span
                v-if="user.type === 'customer'"
                class="text-warning">
                {{ $t('users.customer') }}
              </span>
            </td>
            <td class="align-middle">
              <span
                v-if="user.active === 1"
                class="text-success">
                {{ $t('users.active') }}
              </span>
              <span
                v-if="user.active === 0"
                class="text-danger">
                {{ $t('users.inactive') }}
              </span>
            </td>

            <td class="align-middle">
              <template v-if="!user.groups || !user.groups.length">
                &mdash;
              </template>
              <template v-else>
                <small
                  v-for="(group, subindex) of user.groups"
                  :key="'user-group-' + index + '-' + subindex"
                  class="d-block">
                  {{ group.name }}
                </small>
              </template>
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/uzytkownicy/edycja/' + user.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="user.id === currentUserID || removeInProgress"
                @click.native="removeUser(user.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="users.length === 0">
            <td
              colspan="6"
              class="text-center">
              {{ $t('users.noUsersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!loaded || !currentUserID"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'users',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    accountTypeOptions () {
      return [
        {
          text: this.$t('users.any'),
          value: ''
        },
        {
          text: this.$t('users.admin'),
          value: 'admin'
        },
        {
          text: this.$t('users.worker'),
          value: 'worker'
        },
        {
          text: this.$t('users.warehouseman'),
          value: 'warehouse_manager'
        },
        {
          text: this.$t('users.realWarehouseman'),
          value: 'warehouseman'
        },
        {
          text: this.$t('users.customer'),
          value: 'customer'
        }
      ];
    },
    accountActiveOptions () {
      return [
        {
          text: this.$t('users.accountActive'),
          value: 1
        },
        {
          text: this.$t('users.accountInactive'),
          value: 0
        }
      ];
    },
    userGroupsOptions () {
      return this.userGroups.map(group => ({
        text: group.name,
        value: group.id
      }));
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('ui.enterSearchTerm'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          width: '50%',
          description: this.$t('users.usersSearchPlaceholder')
        },
        {
          key: 'groupID',
          apiKey: 'group_id',
          label: this.$t('settings.userGroups.userGroup'),
          placeholder: this.$t('settings.userGroups.userGroup'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.userGroupsOptions
        },
        {
          key: 'accountType',
          apiKey: 'type',
          label: this.$t('users.accountType'),
          placeholder: this.$t('users.accountType'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.accountTypeOptions
        },
        {
          key: 'accountActive',
          apiKey: 'active',
          label: this.$t('users.isAccountActive'),
          placeholder: this.$t('users.isAccountActive'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.accountActiveOptions
        }
      ];
    }
  },
  data () {
    return {
      users: [],
      loaded: false,
      loadError: false,
      loadedUserGroups: false,
      removeInProgress: false,
      currentUserID: 0,
      userGroups: [],
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.currentUserID = this.$store.getters.getUserID;
    this.loadAdditionalData();

    if (!(this.currentUserID > 0)) {
      this.loadFilteredData();
    }
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/users/items?page=' + this.currentPage,
        listField: 'users',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/users/groups/items',
        loadedKey: 'loadedUserGroups',
        outputKey: 'userGroups',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });
    },
    removeUser (userID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/users/delete/',
        id: userID,
        successTitle: this.$t('users.deleteSuccessTitle'),
        successText: this.$t('users.deleteSuccessText'),
        confirmTitle: this.$t('users.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('users.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>
